import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { audience } from 'api/helpers'

// Must match the Auth0 tenant's Idle Session Lifetime. Auth0 only counts its
// own traffic (sign-ins, token requests) as activity, not use of the app.
const IDLE_SESSION_MS =
  (Number(import.meta.env.VITE_AUTH0_IDLE_SESSION_MINUTES) || 30) * 60 * 1000

const RENEW_ON_ACTIVITY_AFTER_MS = IDLE_SESSION_MS / 3
const WARN_BEFORE_EXPIRY_MS = IDLE_SESSION_MS / 6

const TOUCHED_AT_KEY = 'auth0SessionTouchedAt'

const ACTIVITY_EVENTS = ['pointerdown', 'keydown']

export const tokenExpiresAt = (token) => {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    const { exp } = JSON.parse(atob(payload))
    return exp ? exp * 1000 : null
  } catch {
    return null
  }
}

const currentLocation = () => window.location.pathname + window.location.search

// The API token every request reads from AuthContext, plus when the session
// behind it ends: the token's own expiry or the Auth0 idle timeout, whichever
// comes first. Renewed when the user clicks or types after a third of the idle
// lifetime without Auth0 contact, or continues from the expiry prompt; never on
// a timer, so an unattended tab still times out.
export const useSessionToken = () => {
  const { getAccessTokenSilently, isLoading, loginWithRedirect } = useAuth0()
  const [token, setToken] = useState(null)
  const [touchedAt, setTouchedAt] = useState(null)
  const lastAttemptAt = useRef(0)
  const inFlight = useRef(null)

  const acceptToken = useCallback((newToken) => {
    const now = Date.now()
    localStorage.setItem(TOUCHED_AT_KEY, String(now))
    setTouchedAt(now)
    setToken(newToken)
  }, [])

  const signInAgain = useCallback(
    () => loginWithRedirect({ appState: { targetUrl: currentLocation() } }),
    [loginWithRedirect]
  )

  useEffect(() => {
    ;(async () => {
      try {
        acceptToken(await getAccessTokenSilently({ audience }))
      } catch (error) {
        signInAgain()
      }
    })()
  }, [isLoading, getAccessTokenSilently, acceptToken, signInAgain])

  // Another tab reaching Auth0 keeps the shared session alive for this one too.
  useEffect(() => {
    const syncTouch = (event) => {
      if (event.key === TOUCHED_AT_KEY && event.newValue) setTouchedAt(Number(event.newValue))
    }
    window.addEventListener('storage', syncTouch)
    return () => window.removeEventListener('storage', syncTouch)
  }, [])

  const renew = useCallback(() => {
    lastAttemptAt.current = Date.now()
    if (!inFlight.current) {
      inFlight.current = getAccessTokenSilently({ audience, ignoreCache: true })
        .then((newToken) => {
          acceptToken(newToken)
          return true
        })
        .catch(() => false)
        .finally(() => {
          inFlight.current = null
        })
    }
    return inFlight.current
  }, [getAccessTokenSilently, acceptToken])

  useEffect(() => {
    if (!touchedAt) return

    const renewIfQuiet = () => {
      const lastContact = Math.max(touchedAt, lastAttemptAt.current)
      if (Date.now() - lastContact >= RENEW_ON_ACTIVITY_AFTER_MS) renew()
    }
    const options = { capture: true, passive: true }
    ACTIVITY_EVENTS.forEach((type) => document.addEventListener(type, renewIfQuiet, options))

    return () =>
      ACTIVITY_EVENTS.forEach((type) => document.removeEventListener(type, renewIfQuiet, options))
  }, [touchedAt, renew])

  const continueSession = useCallback(async () => {
    if (!(await renew())) signInAgain()
  }, [renew, signInAgain])

  const deadlines = [tokenExpiresAt(token), touchedAt && touchedAt + IDLE_SESSION_MS]
  const expiresAt = token ? Math.min(...deadlines.filter(Boolean)) : null

  const warnAt = expiresAt && expiresAt - WARN_BEFORE_EXPIRY_MS

  return { token, warnAt, expiresAt, continueSession }
}
